import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Sparkles, Type, Image, Palette, Layout, MessageSquare } from "lucide-react";

export const RegenerateModal = ({ isOpen, onClose, onRegenerate }) => {
  const [options, setOptions] = useState({
    changeTexts: true,
    changeImages: false,
    changeColors: false,
    changeStructure: false,
    customFeedback: ""
  });

  const handleRegenerate = () => {
    onRegenerate(options);
  };

  const toggleOption = (key) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Neue Variante generieren
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Wähle aus, was geändert werden soll
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 sm:py-4">
          <div className="space-y-3 sm:space-y-4">
            {[
              { key: "changeTexts", label: "Andere Texte", desc: "Headlines, Benefits, FAQ neu schreiben", Icon: Type, iconBg: "bg-indigo-100", iconColor: "text-indigo-600" },
              { key: "changeImages", label: "Andere Bilder", desc: "Neues Hero-Bild passend zur Branche", Icon: Image, iconBg: "bg-green-100", iconColor: "text-green-600" },
              { key: "changeColors", label: "Andere Farben", desc: "Stil wechseln (Modern/Seriös/Freundlich)", Icon: Palette, iconBg: "bg-purple-100", iconColor: "text-purple-600" },
              { key: "changeStructure", label: "Andere Struktur", desc: "Komplett anderes Layout & Seitenaufbau", Icon: Layout, iconBg: "bg-amber-100", iconColor: "text-amber-600" },
            ].map(({ key, label, desc, Icon, iconBg, iconColor }) => (
              <div key={key} className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
                  </div>
                  <div>
                    <Label className="font-medium text-slate-900 cursor-pointer text-sm sm:text-base">{label}</Label>
                    <p className="text-xs sm:text-sm text-slate-500">{desc}</p>
                  </div>
                </div>
                <Switch
                  checked={options[key]}
                  onCheckedChange={() => toggleOption(key)}
                />
              </div>
            ))}

            <div className="p-3 sm:p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3 mb-2 sm:mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div>
                  <Label className="font-medium text-slate-900 text-sm sm:text-base">Eigene Anmerkungen</Label>
                  <p className="text-xs sm:text-sm text-slate-500">Optional: Spezifisches Feedback</p>
                </div>
              </div>
              <Textarea
                value={options.customFeedback}
                onChange={(e) => setOptions(prev => ({ ...prev, customFeedback: e.target.value }))}
                placeholder="z.B. 'Mehr auf Privatkunden fokussieren'"
                className="mt-2 resize-none text-sm"
                rows={2}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-4 sm:mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 h-11 sm:h-12 rounded-full"
            >
              Abbrechen
            </Button>
            <Button
              data-testid="confirm-regenerate-btn"
              onClick={handleRegenerate}
              disabled={!options.changeTexts && !options.changeImages && !options.changeColors && !options.changeStructure && !options.customFeedback}
              className="flex-1 h-11 sm:h-12 rounded-full bg-indigo-600 hover:bg-indigo-700"
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              Neu generieren
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegenerateModal;
